// import { useEffect, useMemo, useState } from 'react';
// import type { Program } from '@/types/Program';
//
// type Parsed = Program & { __startMin: number; __endMin: number; __crosses: boolean };
//
// function hhmmToMin(hhmm: string): number {
//     const [h, m] = hhmm.trim().split(':').map(Number);
//     return (h * 60 + m) | 0;
// }
//
// /** Accepts "HH:mm - HH:mm" */
// function parseRange(p: Program): Parsed {
//     const m = p.time.match(/^\s*(\d{2}):(\d{2})\s*-\s*(\d{2}):(\d{2})\s*$/);
//     if (!m) throw new Error(`Invalid time format for program "${p.title}": ${p.time}`);
//     const start = hhmmToMin(`${m[1]}:${m[2]}`);
//     const end = hhmmToMin(`${m[3]}:${m[4]}`);
//     return { ...p, __startMin: start, __endMin: end, __crosses: end <= start };
// }
//
// /** Is now (in minutes) inside [start, end) window. Handles cross-midnight. */
// function inRange(nowMin: number, startMin: number, endMin: number): boolean {
//     if (endMin > startMin) {
//         return nowMin >= startMin && nowMin < endMin;
//     }
//     // Crosses midnight (e.g., 22:00–02:00): now in [start..1440) or [0..end)
//     return nowMin >= startMin || nowMin < endMin;
// }
//
// export function usePrograms(programs: Program[], refreshMs = 60_000) {
//     const [now, setNow] = useState<Date>(() => new Date());
//     useEffect(() => {
//         const id = setInterval(() => setNow(new Date()), refreshMs);
//         return () => clearInterval(id);
//     }, [refreshMs]);
//
//     const parsed = useMemo<Parsed[]>(() => programs.map(parseRange), [programs]);
//     const nowMin = now.getHours() * 60 + now.getMinutes();
//
//     const current = useMemo<Program | null>(() => {
//         return parsed.find(p => inRange(nowMin, p.__startMin, p.__endMin)) ?? null;
//     }, [parsed, nowMin]);
//
//     return { now, current };
// }

// src/hooks/usePrograms.ts
import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "@/lib/api";
import {normalizePage} from "@/lib/paging";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type ProgramId = string;

export type DayOfWeek =
    | "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY"
    | "FRIDAY" | "SATURDAY" | "SUNDAY";

export type Program = {
    id: ProgramId;
    title: string;
    description: string;
    producer: string;              // default producer
    imageUrl: string;
    schedules?: ProgramSchedule[];
    overrides?: ProgramOverride[];
};

export type ProgramSchedule = {
    id: string;
    dayOfWeek: DayOfWeek;
    startTime: string;             // "HH:mm"
    endTime: string;               // "HH:mm"
    producerOverride?: string | null;
};

export type ProgramOverride = {
    id: string;
    date: string;                  // "YYYY-MM-DD"
    startTime: string;             // "HH:mm"
    endTime: string;               // "HH:mm"
    producerOverride?: string | null;
    titleOverride?: string | null;
    descriptionOverride?: string | null;
    imageUrl?: string | null;
    file?: File;
    previewUrlOverride?: string | null; // local preview (created from file)
};

export type SaveProgramInput = {
    id?: ProgramId;
    title: string;
    description: string;
    producer: string;
    file?: File | null;
    schedules?: ScheduleUpsertInput[];
    overrides?: OverrideUpsertInput[];
    onUploadProgress?: (pct: number) => void;
};

export type ScheduleUpsertInput = {
    dayOfWeek: DayOfWeek;
    startTime: string;
    endTime: string;
    producerOverride?: string | null;
};

export type OverrideUpsertInput = {
    date: string;                 // YYYY-MM-DD
    startTime: string;            // HH:mm
    endTime: string;              // HH:mm
    producerOverride?: string | null;
    titleOverride?: string | null;
    descriptionOverride?: string | null;
    file?: File | null;
    onUploadProgress?: (pct: number) => void;
};

export type SortField = "title" | "producer" | "startTime" | "updatedAt";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
    q?: string; // optional search
};

export type ProgramPage = ReturnType<typeof normalizePage<Program>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/programs";
const ADMIN_BASE = "/api/v1/editor/programs";

const DEFAULTS = {
    page: 0,
    size: 12,
    sortBy: "title" as SortField,
    sortOrder: "asc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const programKeys = {
    all: ["programs"] as const,
    list: (params?: HookParams) => [...programKeys.all, "list", params ?? {}] as const,
    one: (id: ProgramId) => [...programKeys.all, "one", id] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): ProgramPage {
    return normalizePage<Program>({page, size, total: 0, content: []});
}

async function fetchPrograms(params: HookParams = {}): Promise<ProgramPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;
    const sort = `${sortBy},${sortOrder}`;
    const q = params.q;

    const {data} = await api.get(PUBLIC_BASE, {params: {page, size, sort, q}});
    if (Array.isArray(data?.content)) return normalizePage<Program>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchProgram(id: ProgramId): Promise<Program> {
    const {data} = await api.get(`${PUBLIC_BASE}/${id}`);
    return data;
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function usePrograms(params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
        q: params?.q,
    };

    return useQuery({
        queryKey: programKeys.list(q),
        queryFn: () => fetchPrograms(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useProgram(id?: ProgramId) {
    return useQuery({
        queryKey: id ? programKeys.one(id) : ["programs", "one", "empty"],
        queryFn: () => fetchProgram(id as ProgramId),
        enabled: Boolean(id),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveProgram() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...programKeys.all, "save"],
        mutationFn: async (payload: SaveProgramInput): Promise<Program> => {
            const form = new FormData();
            form.append("title", payload.title.trim());
            form.append("description", payload.description);
            form.append("producer", payload.producer);
            if (payload.file) form.append("file", payload.file);

            const cfg: any = {headers: {"Content-Type": "multipart/form-data"}};

            if (payload.onUploadProgress) {
                cfg.onUploadProgress = (evt: ProgressEvent) => {
                    if (!evt.total) return;
                    payload.onUploadProgress!(Math.round((evt.loaded * 100) / evt.total));
                };
            }

            if (payload.id) {
                const {data} = await api.put(`${ADMIN_BASE}/${payload.id}`, form, cfg);
                return data;
            } else {
                const {data} = await api.post(ADMIN_BASE, form, cfg);
                return data;
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: programKeys.all});
        },
    });
}

export function useDeleteProgram() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...programKeys.all, "delete"],
        mutationFn: async (id: ProgramId) => {
            await api.delete(`${ADMIN_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            await qc.cancelQueries({queryKey: programKeys.all});

            const snapshots = qc.getQueriesData<unknown>({queryKey: programKeys.all});

            // Optimistically remove from any paged lists
            snapshots.forEach(([key, value]) => {
                const page = value as Partial<ProgramPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: ProgramPage = {
                        ...(page as ProgramPage),
                        items: page.items.filter((p: any) => p.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key as QueryKey, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: programKeys.all});
        },
    });
}

export function useAddSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: [...programKeys.all, "schedule", "add"],
        mutationFn: async ({programId, dto}: { programId: string; dto: ScheduleUpsertInput }) => {
            const {data} = await api.post(`${ADMIN_BASE}/${programId}/schedules`, dto);
            return data; // scheduleId or schedule payload
        },
        onSuccess: () => qc.invalidateQueries({queryKey: programKeys.all}),
    });
}

export function useUpdateSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: [...programKeys.all, "schedule", "update"],
        mutationFn: async ({scheduleId, dto}: { scheduleId: string; dto: ScheduleUpsertInput }) => {
            await api.put(`${ADMIN_BASE}/schedules/${scheduleId}`, dto);
        },
        onSuccess: () => qc.invalidateQueries({queryKey: programKeys.all}),
    });
}

export function useDeleteSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: [...programKeys.all, "schedule", "delete"],
        mutationFn: async (scheduleId: string) => {
            await api.delete(`${ADMIN_BASE}/schedules/${scheduleId}`);
        },
        onSuccess: () => qc.invalidateQueries({queryKey: programKeys.all}),
    });
}

export function useAddOverride() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: [...programKeys.all, "override", "add"],
        mutationFn: async ({programId, dto, file}: { programId: string; dto: OverrideUpsertInput, file?: File }) => {

            const payload = new FormData();
            payload.append("date", dto.date);
            payload.append("startTime", dto.startTime);
            payload.append("endTime", dto.endTime);
            if (dto.producerOverride !== undefined) payload.append("producerOverride", dto.producerOverride ?? "");
            if (dto.titleOverride !== undefined) payload.append("titleOverride", dto.titleOverride ?? "");
            if (dto.descriptionOverride !== undefined) payload.append("descriptionOverride", dto.descriptionOverride ?? "");

            if (file) payload.append("file", file);

            const cfg: any = {headers: {"Content-Type": "multipart/form-data"}};

            const {data} = await api.post(`${ADMIN_BASE}/${programId}/overrides`, payload, cfg);
            return data;
        },
        onSuccess: () => qc.invalidateQueries({queryKey: programKeys.all}),
    });
}

export function useUpdateOverride() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: [...programKeys.all, "override", "update"],
        mutationFn: async ({overrideId, dto, file}: { overrideId: string; dto: OverrideUpsertInput, file?: File }) => {
            const cfg: any = {headers: {"Content-Type": "multipart/form-data"}};

            const payload = new FormData();
            payload.append("date", dto.date);
            payload.append("startTime", dto.startTime);
            payload.append("endTime", dto.endTime);
            if (dto.producerOverride !== undefined) payload.append("producerOverride", dto.producerOverride ?? "");
            if (dto.titleOverride !== undefined) payload.append("titleOverride", dto.titleOverride ?? "");
            if (dto.descriptionOverride !== undefined) payload.append("descriptionOverride", dto.descriptionOverride ?? "");

            if (file) payload.append("file", file);

            return await api.put(`${ADMIN_BASE}/overrides/${overrideId}`, payload, cfg);
        },
        onSuccess: () => qc.invalidateQueries({queryKey: programKeys.all}),
    });
}

export function useDeleteOverride() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: [...programKeys.all, "override", "delete"],
        mutationFn: async (overrideId: string) => {
            await api.delete(`${ADMIN_BASE}/overrides/${overrideId}`);
        },
        onSuccess: () => qc.invalidateQueries({queryKey: programKeys.all}),
    });
}
